import type {
  AggregatedProjectData,
  AICheckRunResult,
  ProjectDataComparisonSnapshot,
} from "@/types/aicheck";

export type TypedProjectDataComparison<TFinding> = Omit<
  ProjectDataComparisonSnapshot,
  "findings"
> & {
  findings: TFinding[];
};

interface ProjectDataComparisonSource {
  comparison?: ProjectDataComparisonSnapshot | null;
  projectData?: AggregatedProjectData | null;
  result?: Pick<
    AICheckRunResult,
    "projectData" | "projectDataComparison"
  > | null;
}

function findingKind(finding: unknown): string | null {
  return finding && typeof finding === "object" && "kind" in finding
    ? String((finding as { kind?: unknown }).kind)
    : null;
}

export function projectDataComparisonFromMergeContext(
  context: AggregatedProjectData["mergeCheckContext"],
): ProjectDataComparisonSnapshot | null {
  if (!context) return null;
  const compareFindings = Array.isArray(context.compareFindings)
    ? context.compareFindings
    : [];
  const finalValidationFindings = Array.isArray(context.finalValidationFindings)
    ? context.finalValidationFindings
    : [];
  const findings = [...compareFindings, ...finalValidationFindings];
  return {
    baseSource: context.baseSource,
    candidateSource: context.candidateSource,
    findings,
    matches: [],
    summary: {
      matches: 0,
      findings: context.summary.findings ?? findings.length,
      conflicts:
        context.summary.conflicts ??
        findings.filter((finding) => findingKind(finding) === "conflict")
          .length,
      missingInCandidate:
        context.summary.missingInCandidate ??
        findings.filter(
          (finding) => findingKind(finding) === "missing_in_candidate",
        ).length,
      extraInCandidate:
        context.summary.extraInCandidate ??
        findings.filter(
          (finding) => findingKind(finding) === "extra_in_candidate",
        ).length,
      finalValidationFindings: context.summary.finalValidationFindings ?? 0,
      baseExisting: context.summary.baseExisting ?? 0,
      candidateIntroduced: context.summary.candidateIntroduced ?? 0,
      mergeInduced: context.summary.mergeInduced ?? 0,
    },
  };
}

export function projectDataComparisonFromSources(
  ...sources: ProjectDataComparisonSource[]
): ProjectDataComparisonSnapshot | null {
  for (const source of sources) {
    if (source.comparison) return source.comparison;
    if (source.result?.projectDataComparison) {
      return source.result.projectDataComparison;
    }
  }

  for (const source of sources) {
    const comparison = projectDataComparisonFromMergeContext(
      source.projectData?.mergeCheckContext ??
        source.result?.projectData.mergeCheckContext,
    );
    if (comparison) return comparison;
  }

  return null;
}

export function typedProjectDataComparison<TFinding>(
  snapshot: ProjectDataComparisonSnapshot | null | undefined,
): TypedProjectDataComparison<TFinding> | null {
  if (!snapshot) return null;
  return {
    ...snapshot,
    findings: snapshot.findings as TFinding[],
  };
}

export function withProjectDataComparison(
  result: AICheckRunResult,
  comparison?: ProjectDataComparisonSnapshot | null,
): AICheckRunResult {
  const nextComparison = projectDataComparisonFromSources({
    comparison,
    result,
  });
  if (!nextComparison || result.projectDataComparison === nextComparison) {
    return result;
  }
  return {
    ...result,
    projectDataComparison: nextComparison,
  };
}
